/**
 * Where a station computer keeps its identity.
 *
 * IndexedDB rather than localStorage because it is the only browser store that
 * can hold a `CryptoKey`. localStorage would force the key to be exported to
 * bytes first, which is exactly what `extractable: false` forbids and exactly
 * what makes the key worth having.
 *
 * Raw IndexedDB rather than a wrapper library: one object store with one record
 * does not repay a dependency, and the offline data layer that will want one
 * is a later phase's decision to make on its own evidence.
 */
import type { DeviceCredential, DeviceIdentity } from "./identity"

const DATABASE_NAME = "eregister-device"
const DATABASE_VERSION = 1
const STORE_NAME = "identity"
const RECORD_KEY = "self"

/** A device has one identity or none, so the record key is a constant. */
type StoredIdentity = DeviceIdentity

export async function readIdentity(): Promise<StoredIdentity | null> {
  const database = await open()
  if (!database) return null

  try {
    return (await request<StoredIdentity | undefined>(
      database.transaction(STORE_NAME, "readonly").objectStore(STORE_NAME).get(RECORD_KEY)
    )) ?? null
  } finally {
    database.close()
  }
}

export async function writeIdentity(identity: StoredIdentity): Promise<void> {
  const database = await open()
  if (!database) throw new Error("This browser has no usable IndexedDB, so a device identity cannot be kept.")

  try {
    await request(database.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).put(identity, RECORD_KEY))
  } finally {
    database.close()
  }
}

/** Refreshed on every heartbeat, so an administrator's change reaches the device within one interval. */
export async function updateCredential(credential: DeviceCredential): Promise<void> {
  const identity = await readIdentity()
  if (!identity) return

  await writeIdentity({ ...identity, credential })
}

/**
 * Used when the server says this computer is no longer itself — revoked, or
 * enrolled afresh. Leaving a dead key behind would mean every request failing
 * with a signature error rather than the machine reporting that it needs
 * enrolling again.
 */
export async function clearIdentity(): Promise<void> {
  const database = await open()
  if (!database) return

  try {
    await request(database.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME).delete(RECORD_KEY))
  } finally {
    database.close()
  }
}

/**
 * Resolves to null rather than throwing when IndexedDB is unavailable — a
 * private-browsing window, or a browser with storage disabled. A page that is
 * not on a nominated computer must still load.
 */
function open(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") return Promise.resolve(null)

  return new Promise(resolve => {
    let opening: IDBOpenDBRequest
    try {
      opening = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)
    } catch {
      resolve(null)
      return
    }

    opening.onupgradeneeded = () => {
      if (!opening.result.objectStoreNames.contains(STORE_NAME)) opening.result.createObjectStore(STORE_NAME)
    }
    opening.onsuccess = () => resolve(opening.result)
    opening.onerror = () => resolve(null)
    opening.onblocked = () => resolve(null)
  })
}

function request<T>(operation: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    operation.onsuccess = () => resolve(operation.result)
    operation.onerror = () => reject(operation.error ?? new Error("IndexedDB request failed"))
  })
}
